import "./RunMyTransport.css";
import {
  FaTruck,
  FaRoute,
  FaFileInvoiceDollar,
  FaChartBar,
  FaUsers,
  FaGasPump,
  FaClock,
  FaMoneyBillWave,
  FaShieldAlt,
  FaCloud,
  FaCheckCircle,
  FaCar,
  FaUserTie,
  FaClipboardList,
  FaBoxes,
  FaWallet,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import truckVideo from "../../assets/truck-bg.mp4";

import { useEffect } from "react";

import ProductNavbar from "../Navbar/ProductNavbar";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

function RunMyTransport() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const features = [
    {
      icon: <FaTruck />,
      title: "Fleet Management",
      desc: "Manage vehicles, maintenance schedules, permits, and fleet lifecycle from a centralized platform.",
    },
    {
      icon: <FaRoute />,
      title: "Trip Management",
      desc: "Plan, assign, and monitor transport trips efficiently from start to completion.",
    },
    {
      icon: <FaFileInvoiceDollar />,
      title: "Invoice Management",
      desc: "Generate invoices automatically and maintain accurate billing records.",
    },
    {
      icon: <FaGasPump />,
      title: "Fuel Management",
      desc: "Track diesel consumption, fuel expenses, and vehicle efficiency.",
    },
    {
      icon: <FaChartBar />,
      title: "Reports & Analytics",
      desc: "Generate operational and financial reports for better business decisions.",
    },
    {
      icon: <FaUsers />,
      title: "User Management",
      desc: "Secure role-based access with JWT authentication and permission controls.",
    },
  ];

  const benefits = [
    {
      icon: <FaClock />,
      title: "Save Time",
      desc: "Automate daily transport operations and reduce manual effort.",
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Reduce Costs",
      desc: "Monitor fuel, maintenance, and operational expenses effectively.",
    },
    {
      icon: <FaRoute />,
      title: "Real-Time Monitoring",
      desc: "Track trips and vehicle movement with real-time visibility.",
    },
    {
      icon: <FaChartBar />,
      title: "Business Insights",
      desc: "Make informed decisions using detailed reports and analytics.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure Access",
      desc: "Protect your business with JWT authentication and role-based permissions.",
    },
    {
      icon: <FaCloud />,
      title: "Cloud-Based",
      desc: "Access your transport operations securely from anywhere, anytime.",
    },
  ];

  const modules = [
    {
      icon: <FaCar />,
      title: "Vehicle Management",
      desc: "Manage vehicle information, documents, and maintenance schedules.",
    },
    {
      icon: <FaUserTie />,
      title: "Driver Management",
      desc: "Manage driver profiles, licenses, and trip assignments.",
    },
    {
      icon: <FaRoute />,
      title: "Trip & Order Management",
      desc: "Manage customer orders, create trips, and monitor transport operations.",
    },
    {
      icon: <FaUsers />,
      title: "Customer Management",
      desc: "Manage customer information and transport orders efficiently.",
    },
    {
      icon: <FaTruck />,
      title: "Vendor Management",
      desc: "Manage transport vendors, contractors, and partner details.",
    },
    {
      icon: <FaBoxes />,
      title: "Inventory Management",
      desc: "Track spare parts, stock availability, and inventory movement.",
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Expense Management",
      desc: "Track operational, maintenance, and miscellaneous business expenses.",
    },
    {
      icon: <FaWallet />,
      title: "Payment Management",
      desc: "Track customer receipts and vendor payments in one place.",
    },
    {
      icon: <FaClipboardList />,
      title: "Financial Reporting",
      desc: "Generate comprehensive financial reports to monitor business performance.",
    },
  ];

  const reports = [
    "Trip Report",
    "Vehicle Financial Report",
    "Expense Report",
    "Diesel Report",
    "Payment Report",
    "Advance Payment Report",
    "Inventory Report",
    "Profit & Loss Report",
  ];

  const workflow = [
    "Receive Customer Order",
    "Create Trip",
    "Assign Vehicle",
    "Assign Driver",
    "Execute Trip",
    "Generate Invoice",
    "Track Payment",
    "Analyze Reports",
  ];

  const standardClients = [
    {
      name: "RK Transport",
    },
    {
      name: "SRT Transport",
    },
    {
      name: "Basava Transport",
    },
    {
      name: "Nandi Transport",
    },
  ];

  const ultraTechClients = [
    {
      name: "SGT Transport",
    },
    {
      name: "Rashtrakuta Transport",
    },
  ];

  return (
    <>
      <ProductNavbar />

      <div className="rmt-page">
        {/* Hero */}
        <section className="hero">
          <video className="hero-video" autoPlay muted loop playsInline>
            <source src={truckVideo} type="video/mp4" />
          </video>

          <div className="hero-overlay"></div>

          <div className="hero-content">
            <h1>Run My Transport</h1>

            <p>
              Simplify fleet operations, automate transport workflows, and gain
              complete control over your logistics business with Run My
              Transport — a modern cloud-based Transport Management System built
              for efficiency, visibility, and business growth.
            </p>

            <a
              href="https://www.runmytransport.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="login-btn"
            >
              Access Application
            </a>
          </div>
        </section>

        {/* About */}

        <section className="section">
          <h2>About Run My Transport</h2>

          <div className="section-description">
            <p>
              Run My Transport is a cloud-based Transport Management System
              (TMS) built to streamline logistics and transportation operations
              for businesses of all sizes.
            </p>

            <br />

            <p>
              The platform enables organizations to efficiently manage vehicles,
              drivers, customers, vendors, trips, inventory, invoices, payments,
              expenses, and financial reporting from a single centralized
              dashboard—improving productivity while reducing manual effort and
              operational costs.
            </p>
          </div>
        </section>

        {/* Features */}

        <section className="section">
          <h2>Key Features</h2>

          <div className="feature-grid">
            {features.map((item, index) => (
              <div className="feature-card" key={index}>
                {item.icon}

                <h3>{item.title}</h3>

                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}

        <section className="section light-bg">
          <h2>Business Benefits</h2>

          <div className="benefit-grid">
            {benefits.map((item, index) => (
              <div className="benefit-card" key={index}>
                {item.icon}

                <h3>{item.title}</h3>

                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Modules */}

        <section className="section">
          <h2>Modules</h2>

          <div className="module-grid">
            {modules.map((module, index) => (
              <div className="module-card" key={index}>
                {module.icon}

                <h3>{module.title}</h3>

                <p>{module.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Clients */}

        <section className="section light-bg">
          <h2>Our Clients</h2>

          <p className="section-description">
            Run My Transport powers logistics operations for transport companies
            across multiple industries. The platform supports both Shree Cement
            and UltraTech transport workflows, delivering a scalable solution
            for diverse business needs.
          </p>

          <div className="client-container">
            <div className="client-category">
              <h3> SHREE CEMENT </h3>

              <div className="client-slider">
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  navigation
                  pagination={{ clickable: true }}
                  autoplay={{
                    delay: 2500,
                    disableOnInteraction: false,
                  }}
                  loop={true}
                  spaceBetween={25}
                  breakpoints={{
                    0: {
                      slidesPerView: 1,
                    },
                    768: {
                      slidesPerView: 2,
                    },
                    1024: {
                      slidesPerView: 3,
                    },
                  }}
                >
                  {standardClients.map((client, index) => (
                    <SwiperSlide key={index}>
                      <div className="client-card">
                        <h4>{client.name}</h4>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>

            <div className="client-category">
              <h3> ULTRATECH CEMENT </h3>

              <div className="client-slider">
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  navigation
                  pagination={{ clickable: true }}
                  autoplay={{
                    delay: 2500,
                    disableOnInteraction: false,
                  }}
                  loop={true}
                  spaceBetween={25}
                  breakpoints={{
                    0: {
                      slidesPerView: 1,
                    },
                    768: {
                      slidesPerView: 2,
                    },
                    1024: {
                      slidesPerView: 2,
                    },
                  }}
                >
                  {ultraTechClients.map((client, index) => (
                    <SwiperSlide key={index}>
                      <div className="client-card">
                        <h4>{client.name}</h4>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </section>
        {/* Workflow */}

        <section className="section light-bg">
          <h2>How It Works</h2>

          <div className="workflow">
            {workflow.map((step, index) => (
              <div className="workflow-step" key={index}>
                <div className="workflow-item">
                  <div className="workflow-circle">{index + 1}</div>

                  <h4>{step}</h4>
                </div>

                {index !== workflow.length - 1 && (
                  <div className="workflow-arrow">➜</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Reports */}

        <section className="section light-bg">
          <h2>Comprehensive Reports</h2>

          <div className="report-grid">
            {reports.map((report, index) => (
              <div className="report-card" key={index}>
                <FaCheckCircle />

                <span>{report}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Security */}

        <section className="section">
          <h2>Security & Reliability</h2>

          <div className="security-grid">
            <div className="security-card">
              <FaShieldAlt />

              <h3>JWT Authentication</h3>

              <p>
                Secure user authentication using JSON Web Tokens for safe and
                reliable access.
              </p>
            </div>

            <div className="security-card">
              <FaUsers />

              <h3>Role-Based Access</h3>

              <p>
                Control user permissions with role-based authorization for
                secure business operations.
              </p>
            </div>

            <div className="security-card">
              <FaCloud />

              <h3>Cloud-Based Platform</h3>

              <p>
                Reliable cloud deployment with secure REST APIs, high
                availability, and easy accessibility.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose */}

        <section className="section light-bg">
          <h2>Why Choose Run My Transport?</h2>

          <div className="why-grid">
            <div>✔ Complete Transport ERP Platform</div>

            <div>✔ Intuitive & Easy-to-Use Dashboard</div>

            <div>✔ Efficient Fleet & Driver Management</div>

            <div>✔ End-to-End Trip Tracking</div>

            <div>✔ Smart Inventory Management</div>

            <div>✔ Comprehensive Financial Reporting</div>

            <div>✔ Secure JWT Authentication</div>

            <div>✔ Cloud-Based Deployment</div>

            <div>✔ Scalable & Reliable Architecture</div>
          </div>
        </section>

        {/* CTA */}

        <section className="cta">
          <h2>Ready to Transform Your Transport Business?</h2>

          <p>
            Request a personalized product demonstration and discover how Run My
            Transport helps simplify fleet management, automate logistics
            operations, and improve business efficiency—all from a single
            cloud-based Transport Management System.
          </p>

          <button className="contact-btn" aria-label="Contact Run My Transport">
            Contact Us
          </button>

          <div className="contact-info">
            <div className="info-box">
              <FaEnvelope />

              <h4>Email Us</h4>

              <a href="mailto:contactus@belageri.com" className="contact-link">
                contactus@belageri.com
              </a>
            </div>

            <div className="info-box">
              <FaPhoneAlt />

              <h4>Call Us</h4>

              <a href="tel:+919449974910" className="contact-link">
                +91 9449974910
              </a>
            </div>

            <div className="info-box">
              <FaMapMarkerAlt />

              <h4>Visit Us</h4>

              <a
                href="https://maps.app.goo.gl/NrJS3Z3UmEAnaLFg6"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                Belageri Technologies Pvt Ltd <br />
                NO-11-1400/136/7,
                <br />
                Akkamahadevi Colony, KHB,
                <br />
                Opp. High Court,
                <br />
                Kalaburagi, Karnataka - 585103
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}

        <footer className=" prod-footer">
          <h3>Run My Transport</h3>

          <p>
            Empowering transport businesses with smarter fleet management,
            logistics automation, and actionable business insights.
          </p>

          <p>© 2026 Run My Transport. All Rights Reserved.</p>
        </footer>
      </div>
    </>
  );
}
export default RunMyTransport;
